import { useState } from "react";
import { Play, Pause } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSoundtrack, SOUNDTRACK_TITLE } from "@/contexts/SoundtrackContext";

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const HeroSoundtrack = () => {
  const { isPlaying, toggle, currentTime, duration } = useSoundtrack();
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="mb-10 md:mb-12 max-w-md">
      <div className="flex items-start gap-4">
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? `Pause ${SOUNDTRACK_TITLE}` : `Play ${SOUNDTRACK_TITLE}`}
          className="group mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[hsl(40_55%_70%)]/40 text-[hsl(40_55%_72%)] transition-all duration-300 hover:border-[hsl(40_55%_70%)]/80 hover:text-[hsl(40_55%_82%)] hover:bg-[hsl(40_55%_55%)]/10"
        >
          {isPlaying ? (
            <Pause className="h-4 w-4" strokeWidth={1.5} />
          ) : (
            <Play className="h-4 w-4 translate-x-[1px]" strokeWidth={1.5} />
          )}
        </button>

        <div className="min-w-0">
          <p
            className="font-serif italic text-[15px] md:text-base text-[hsl(40_45%_88%)] leading-snug"
            style={{ textShadow: "0 1px 12px hsl(150 35% 6% / 0.8)" }}
          >
            The Sound of Sugar Town Organics —{" "}
            <span className="text-[hsl(40_55%_72%)] not-italic">{SOUNDTRACK_TITLE}</span>
          </p>
          <p
            className="font-sans text-[11px] md:text-xs tracking-wide text-[hsl(40_30%_82%)]/80 mt-1.5 leading-relaxed"
            style={{ textShadow: "0 1px 10px hsl(150 35% 6% / 0.8)" }}
          >
            A musical reflection of Caribbean heritage, healing, resilience, and legacy.
          </p>

          {/* Progress only appears once the track is playing */}
          {(isPlaying || currentTime > 0) && (
            <div className="mt-3 flex items-center gap-2.5">
              <div className="h-px flex-1 bg-[hsl(40_33%_94%)]/20 overflow-hidden">
                <div
                  className="h-full bg-[hsl(40_55%_65%)]/80 transition-[width] duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="font-sans text-[10px] tabular-nums tracking-wider text-[hsl(40_30%_82%)]/70">
                {formatTime(currentTime)}
              </span>
            </div>
          )}

          <button
            type="button"
            onClick={() => setLearnMoreOpen(true)}
            className="mt-3 font-sans text-[10px] uppercase tracking-[0.28em] text-[hsl(40_45%_80%)]/70 underline underline-offset-4 decoration-[hsl(40_55%_65%)]/40 transition-colors hover:text-[hsl(40_55%_78%)]"
          >
            Learn More
          </button>
        </div>
      </div>

      <Dialog open={learnMoreOpen} onOpenChange={setLearnMoreOpen}>
        <DialogContent className="max-w-lg bg-[hsl(40_30%_97%)] border-[hsl(40_45%_70%)]/30">
          <DialogHeader>
            <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-[hsl(40_50%_42%)] mb-2">
              The Sound of Sugar Town Organics
            </p>
            <DialogTitle className="font-serif text-2xl md:text-3xl text-[hsl(150_30%_14%)]">
              {SOUNDTRACK_TITLE}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 font-sans text-[15px] leading-relaxed text-[hsl(150_15%_28%)]">
            <p>
              Rooted in the Sun was created as a musical expression of the Sugar Town
              Organics story.
            </p>
            <p>
              Inspired by Caribbean landscapes, family traditions, healing plants,
              resilience, and the enduring connection between people and place, the song
              serves as the soundtrack of the Sugar Town Organics ecosystem.
            </p>
            <p>
              Whether exploring The Archive, discovering the traditions behind our wellness
              practices, visiting the Organics Shoppe, or experiencing one of our brands,
              this music reflects the spirit that connects them all.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeroSoundtrack;
